import { useState, useEffect, useRef, forwardRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Props } from '@ckeditor/ckeditor5-react/dist/ckeditor';

import {
	DecoupledEditor,
	AccessibilityHelp,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	Autosave,
	BalloonToolbar,
	//Base64UploadAdapter,
	BlockQuote,
	BlockToolbar,
	Bold,
	Code,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	HorizontalLine,
	ImageBlock,
	ImageCaption,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	SimpleUploadAdapter,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	//Markdown,
	MediaEmbed,
	Mention,
	//Minimap,
	PageBreak,
	Paragraph,
	//PasteFromMarkdownExperimental,
	RemoveFormat,
	SelectAll,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Style,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo,
    EditorConfig,
	Editor
} from 'ckeditor5';
import FileManager from "./FileManager/FileManager"; 
/// @ts-ignore
import translations from 'ckeditor5/translations/pt-br.js';

import 'ckeditor5/ckeditor5.css';
import './EditorBasico.css';

export type EditorBasicoPropsType = Omit<Props<Editor>, "editor"> & {
};

const EditorBasico = forwardRef<CKEditor<DecoupledEditor>, EditorBasicoPropsType>(function EditorBasico (
	{
		onReady,
		onAfterDestroy,
		config,
		...rest
	}, 
	ref
) {
	const editorContainerRef = useRef<HTMLDivElement|null>(null);
	const editorMenuBarRef = useRef<HTMLDivElement|null>(null);
	const editorToolbarRef = useRef<HTMLDivElement|null>(null);
	const editorRef = useRef<HTMLDivElement|null>(null);
	const [isLayoutReady, setIsLayoutReady] = useState(false);

	useEffect(() => {
		setIsLayoutReady(true);

		return () => setIsLayoutReady(false);
	}, []);

    const editorConfig = {
		toolbar: {
			items: [
				'heading', // Titulos
				'style', // Estilos de conteudo
				'|',
				'fontSize', // Tamanho da fonte
				'fontFamily', // Familia da fonte
				'fontColor', // Cor da fonte
				'fontBackgroundColor', // Background da fonte
				'alignment', // Alinhamento de texto
				{
					label : "Extra",
					withText : "Extra",
					icon : false,
					items : [
						'bold',
						'italic',
						'underline',
						'strikethrough',
						'code',
						'removeFormat',
						'blockQuote'
					]
				},
				'|',
				'link',
				'insertTable',
				//'insertImage',
				'FileManager',
				'insertImageViaUrl',
				'mediaEmbed'
			],
			shouldNotGroupWhenFull: false // Se deve ou não exibir mais de uma fileira de configuracoes na toolbar
		},
		plugins: [
			//Internos
			FileManager,

			AccessibilityHelp,
			Alignment,
			Autoformat,
			AutoImage,
			AutoLink,
			Autosave,
			BalloonToolbar,
			//Base64UploadAdapter,
			SimpleUploadAdapter,
			BlockQuote,
			BlockToolbar,
			Bold,
			Code,
			Essentials,
			FindAndReplace,
			FontBackgroundColor,
			FontColor,
			FontFamily,
			FontSize,
			GeneralHtmlSupport,
			Heading,
			HorizontalLine,
			ImageBlock,
			ImageCaption,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			//Markdown,
			MediaEmbed,
			Mention,
			PageBreak,
			Paragraph,
			//PasteFromMarkdownExperimental,
			RemoveFormat,
			SelectAll,
			SpecialCharacters,
			SpecialCharactersArrows,
			SpecialCharactersCurrency,
			SpecialCharactersEssentials,
			SpecialCharactersLatin,
			SpecialCharactersMathematical,
			SpecialCharactersText,
			Strikethrough,
			Style,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			TodoList,
			Underline,
			Undo
		],
		balloonToolbar: ['bold', 'italic', '|', 'link', 'insertImage', '|', 'bulletedList', 'numberedList'],
		blockToolbar: [
			'fontSize',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'bold',
			'italic',
			'|',
			'link',
			'insertImage',
			'insertTable',
			'|',
			'bulletedList',
			'numberedList',
			'outdent',
			'indent'
		],
		fontFamily: {
			supportAllValues: true
		},
		fontSize: {
			options: [ 10, 12, 14, 'default', 18, 20, 22 ],
			supportAllValues: true
		},
		heading: {
			options: [
				{
					model: 'paragraph',
					title: 'Paragraph',
					class: 'ck-heading_paragraph'
				},
				{
					model: 'heading1',
					view: 'h1',
					title: 'Heading 1',
					class: 'ck-heading_heading1'
				},
				{
					model: 'heading2',
					view: 'h2',
					title: 'Heading 2',
					class: 'ck-heading_heading2'
				},
				{
					model: 'heading3',
					view: 'h3',
					title: 'Heading 3',
					class: 'ck-heading_heading3'
				},
				{
					model: 'heading4',
					view: 'h4',
					title: 'Heading 4',
					class: 'ck-heading_heading4'
				},
				{
					model: 'heading5',
					view: 'h5',
					title: 'Heading 5',
					class: 'ck-heading_heading5'
				},
				{
					model: 'heading6',
					view: 'h6',
					title: 'Heading 6',
					class: 'ck-heading_heading6'
				}
			]
		},
		htmlSupport: {
			allow: [
				{
					name: /^.*$/,
					styles: true,
					attributes: true,
					classes: true
				}
			]
		},
		image: {
			toolbar: [
				'toggleImageCaption',
				'imageTextAlternative',
				'|',
				'imageStyle:alignBlockLeft',
				'imageStyle:block',
				'imageStyle:alignBlockRight',
				'|',
				'resizeImage'
			],
			styles: {
				options: ['alignBlockLeft', 'block', 'alignBlockRight']
			}
		},
		initialData: "", // Valor inicial
		language: 'pt-br',
		link: {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		},
		simpleUpload : {
			//uploadUrl : process.env.NODE_ENV === "development" ? 'https://localhost:7123/api/teste' : `${window.location.origin}/api/api`,
			
			withCredentials : true,
			headers: {
				'X-CSRF-TOKEN': 'CSRF-Token',
				Authorization: 'Bearer Teste Token de acesso'
			}
		},
		list: {
			properties: {
				styles: true,
				startIndex: true,
				reversed: true
			}
		},
		mention: {
			feeds: [
				{
					marker: '#',
					feed: [
						{
							id : "#NOMEVIAGEM#",
							text : "#NOMEVIAGEM#"
						}
					]
				}
			]
		},
		menuBar: {
			isVisible: false
		},
		placeholder: 'Digite ou cole seu conteúdo aqui!',
		style: {
			definitions: [
				{
					name: 'Article category',
					element: 'h3',
					classes: ['category']
				},
				{
					name: 'Title',
					element: 'h2',
					classes: ['document-title']
				},
				{
					name: 'Subtitle',
					element: 'h3',
					classes: ['document-subtitle']
				},
				{
					name: 'Info box',
					element: 'p',
					classes: ['info-box']
				},
				{
					name: 'Side quote',
					element: 'blockquote',
					classes: ['side-quote']
				},
				{
					name: 'Marker',
					element: 'span',
					classes: ['marker']
				},
				{
					name: 'Spoiler',
					element: 'span',
					classes: ['spoiler']
				},
				{
					name: 'Code (dark)',
					element: 'pre',
					classes: ['fancy-code', 'fancy-code-dark']
				},
				{
					name: 'Code (bright)',
					element: 'pre',
					classes: ['fancy-code', 'fancy-code-bright']
				}
			]
		},
		table: {
			contentToolbar : ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
		},
		translations : [translations],
		...(config ?? {}) as EditorConfig
	} as EditorConfig;

	return (
		<div>
			<div className="main-container">
				<div className="editor-container editor-container_document-editor" ref={editorContainerRef}>
					<div className="editor-container__menu-bar" ref={editorMenuBarRef}></div>
					<div className="editor-container__toolbar" ref={editorToolbarRef}></div>
					<div className="editor-container__editor-wrapper">
						<div className="editor-container__editor">
							<div ref={editorRef}>
								{isLayoutReady && (
									<CKEditor
										onReady={editor => {
											if (editor.ui.view.toolbar.element != null)
												editorToolbarRef?.current?.appendChild(editor.ui.view.toolbar.element);
											if (editor.ui.view.menuBarView.element != null)
												editorMenuBarRef?.current?.appendChild(editor.ui.view.menuBarView.element);
											onReady?.(editor);
										}}
										onAfterDestroy={(editor) => {
											Array.from(editorToolbarRef?.current?.children ?? []).forEach(child => child.remove());
											Array.from(editorMenuBarRef?.current?.children ?? []).forEach(child => child.remove());
											onAfterDestroy?.(editor);
										}}
										editor={DecoupledEditor}
										config={editorConfig}
										ref={ref}
										{...rest}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

export default EditorBasico;